import { useEffect, useState } from 'react';
import {
    useGetIDsFromURL,
    verifySingleStack,
    getDeviceType,
} from 'components/DeviceDetails/helper';
import { useDeviceState } from 'components/DeviceDetails/service';
import { useGetSiteData } from 'components/SiteDetails/helper';
import { ProfileTypes, DeviceTypes } from 'components/DeviceDetails/constants';
import {
    getMasterConfig,
    getPackConfig,
} from 'components/DeviceDetails/EditDeviceProfile/helper';
import { useDevice } from 'components/DeviceDetails/service';
import { useJwtToken } from 'helper';
import { useQuery } from 'react-query';
import { useGetWarranties } from 'components/WarrantyList/helper';
import dayjs from 'dayjs';

export const useProfileData = () => {
    const accessToken = useJwtToken();
    const { Thing_ID, Site_ID, BMS_ID, Module_ID } = useGetIDsFromURL();
    const { data: device, isLoading: deviceLoading } = useDevice(Thing_ID);
    const { data: site, isLoading: siteLoading } = useGetSiteData(Site_ID);
    const { SINGLE_BMS_ID } = verifySingleStack(device);
    const deviceType = getDeviceType(device, BMS_ID, Module_ID);
    const deviceOnline = useOnlineStatus(device, deviceType);
    const masterId = SINGLE_BMS_ID ?? BMS_ID;
    const packId = Module_ID ?? BMS_ID;
    const bmsConfigData = getMasterConfig(
        device?.Config?.data?.MASTER_SNS,
        masterId
    );
    const moduleConfigData = getPackConfig(bmsConfigData?.PACKS, packId);
    const { data: warranty, isLoading: warrantyLoading } = useGetWarranties(
        'desc',
        'SerialNumber',
        1,
        packId ?? masterId ?? device?.SerialNumber,
        'SerialNumber'
    );
    const profileType =
        deviceType === DeviceTypes.DEFAULT ? ProfileTypes.Sites : deviceType;

    const profileData = useQuery(
        ['profileData', profileType, Thing_ID || Site_ID],
        async () => {
            if (profileType === ProfileTypes.Sites) {
                return site;
            } else {
                const {
                    WarrantyStart = null,
                    WarrantyEnd = null,
                    SiteLink = null,
                } = warranty?.results[0] ?? {};
                return {
                    ...device,
                    CustomerEmail: device?.SiteInfo?.CustomerInfo?.Email ?? '',
                    Installer: device?.SiteInfo?.InstallerInfo?.Name ?? '',
                    InstallDate:
                        dayjs(device?.SiteInfo?.createdAt).format(
                            'MM/DD/YYYY'
                        ) ?? '',
                    InstallCompany: device?.SiteInfo?.CompanyInfo?.Name ?? '',
                    WarrantyStart,
                    WarrantyEnd,
                    SiteLink:
                        SiteLink ||
                        (device?.Site_ID ? `sites/${device.Site_ID}` : null),
                };
            }
        },
        {
            enabled:
                !!accessToken &&
                ((profileType === ProfileTypes.Sites && !!Site_ID && !!site) || // Ensure site, device, and warranty are defined
                    (profileType !== ProfileTypes.Sites &&
                        !!Thing_ID &&
                        !!device &&
                        warranty !== undefined)),
        }
    );
    return {
        ...profileData,
        loading:
            deviceLoading ||
            siteLoading ||
            warrantyLoading ||
            profileData.isLoading
                ? true
                : false,
        deviceType,
        deviceOnline,
        bmsConfigData,
        moduleConfigData,
    };
};

//@todo we should refactor and use a get online hook
//and that should optimize for both master's and module devices
//we don't need to make another api call for a master
export const useOnlineStatus = (device, deviceType) => {
    const [isOnline, setIsOnline] = useState(false);
    const { data: deviceState } = useDeviceState();

    useEffect(() => {
        if (deviceType !== 'MODULE') {
            const currentTime = Date.now() / 1000;
            const BMSStatus =
                device?.ts && device?.ts + 60 * 10 > currentTime
                    ? 'online'
                    : 'offline';
            setIsOnline(BMSStatus);
        } else {
            const moduleStatus = deviceState?.isOnline;
            setIsOnline(moduleStatus);
        }
    }, [deviceType, device, deviceState]);
    return isOnline;
};

export const checkStatusIsOnline = (stat) => {
    // Define the time limit for considering the device as online (5 minutes ago from now)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    if (stat?.isodate_utc) {
        const lastStatTimestamp = new Date(stat.isodate_utc).getTime();
        if (lastStatTimestamp >= fiveMinutesAgo) {
            return true;
        }
    }
    return false;
};
